import {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type Ref,
} from 'react';
import { fold } from '@/domain';
import { CloseIcon } from '@/ui/icons';
import { narrow } from './comboboxMatch';
import { modalStyles } from './Modal';
import styles from './Combobox.module.css';

/** One row of the list. */
export interface ComboboxOption {
  /** What tells one row from another: a code, an id. */
  key: string;
  /** The row's words, and what the box holds once the row is chosen. */
  label: string;
  /** Quieter words at the end of the row: a calling code, the region a city is in. */
  detail?: string | null;
  /** More the row is found by beside its label: a country's code, `+971`. */
  alsoFoundBy?: readonly string[];
}

/** The words the box says itself. Its owner hands them in, from the strings. */
export interface ComboboxWords {
  /** The control that empties the box, named for the field: "Clear City". */
  clear: (field: string) => string;
  /** The last row, for what was typed and is not in the list: "Use 'Al Barsha'". */
  useTyped: (text: string) => string;
  /** Nothing in the list matches what was typed. */
  none: string;
  /** The list is on its way. */
  loading: string;
}

export interface ComboboxProps {
  /** The input's id, which the label names. One is made when none is given. */
  id?: string;
  label: string;
  /**
   * The words in the box while nobody is typing: the chosen row's label, or,
   * where typing is allowed (`onType`), whatever was typed.
   */
  value: string;
  options: readonly ComboboxOption[];
  /** A row was chosen. */
  onChoose: (option: ComboboxOption) => void;
  /**
   * Makes what is typed a value of its own, told on every keystroke: a city
   * the list does not have. Without it the box only chooses: typing narrows
   * the list, and what was typed is dropped when the field is left.
   */
  onType?: (text: string) => void;
  /**
   * The rows already answer what is typed, as a list fetched while it is typed
   * does: the box shows them as they are and does not narrow them again.
   */
  fetched?: boolean;
  /** The rows for what is typed have not arrived yet. */
  loading?: boolean;
  /**
   * Offers what was typed as the last row, "Use 'Al Barsha'", whenever no row
   * says exactly that. Only with `onType`. Hold it back until the list for
   * what was typed has arrived, or the offer is made of a city the list has.
   */
  offerTyped?: boolean;
  /** That last row was chosen. The text is already the value: this is for what else follows. */
  onUseTyped?: (text: string) => void;
  /** Marks the label, and the input for a screen reader. Whether it is filled in is the form's to check. */
  required?: boolean;
  /** Draws a control that empties the box, while it holds something. */
  onClear?: () => void;
  /** What is wrong with the field, under it. */
  error?: string | null;
  /** A line under the field: an example, or what else may be typed. */
  hint?: string | null;
  placeholder?: string;
  words: ComboboxWords;
  /** Takes the focus as soon as it is drawn: for a box that a button reveals. */
  focusOnMount?: boolean;
  /** The field took the focus: the moment its list is first wanted. */
  onOpen?: () => void;
  /** The field was left, or its list put away with Escape, with nothing chosen. */
  onDismiss?: () => void;
  inputRef?: Ref<HTMLInputElement>;
  /** Passed to the input: how a phone's keyboard opens for it. */
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  autoCapitalize?: InputHTMLAttributes<HTMLInputElement>['autoCapitalize'];
  className?: string;
}

/** The nearest box round an element that scrolls up and down: a sheet's content, or the page's own. */
function scrollerOf(element: HTMLElement): HTMLElement | null {
  for (let node = element.parentElement; node !== null; node = node.parentElement) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight) {
      return node;
    }
  }
  return null;
}

/**
 * A box that chooses from a list by typing: a country, a region, a city, a
 * calling code. The request of 6 October 2026 (fifth), section 2: every
 * dropdown of an address is searchable, by any part of a name and with or
 * without its accents, and a city or an area the list does not have can be
 * typed and used as it is.
 *
 * To a screen reader it is a combobox with a list, as the doctor and the
 * patient on a case are (`RecordField` in the intake form, which this follows
 * key for key): the arrow keys move through the rows, Enter chooses the one
 * they are on, Escape puts the list away and stops there, so the sheet the
 * field is in stays open.
 *
 * It works two ways. With no `onType` it only chooses: the box shows what is
 * chosen, typing narrows the list with the best match already marked for
 * Enter, and leaving the field drops what was typed. With `onType` what is
 * typed is itself the value, and the list is an offer beside it: its last row
 * can be "Use 'what was typed'", which says in so many words that a name the
 * list does not have will do.
 *
 * The list is in the flow of the form and never over it, so nothing a sheet
 * is clipped by can cut it off. It is five rows tall at most and scrolls
 * inside itself, and when it opens the box round the form is scrolled just
 * far enough to show all of it, or, where a phone's keyboard leaves too little
 * room for that, to put the field at the top with as much of the list as fits
 * under it. A row keeps the focus in the input while it is pressed.
 */
export function Combobox({
  id,
  label,
  value,
  options,
  onChoose,
  onType,
  fetched = false,
  loading = false,
  offerTyped = false,
  onUseTyped,
  required = false,
  onClear,
  error = null,
  hint = null,
  placeholder,
  words,
  focusOnMount = false,
  onOpen,
  onDismiss,
  inputRef,
  inputMode,
  autoCapitalize,
  className,
}: ComboboxProps) {
  const made = useId();
  const inputId = id ?? `${made}-input`;
  const ids = { list: `${made}-list`, hint: `${made}-hint`, problem: `${made}-problem` };
  const free = onType !== undefined;

  const [focused, setFocused] = useState(false);
  // Put away with Escape or by a choice, until the person types or comes back to the field.
  const [dismissed, setDismissed] = useState(false);
  const [active, setActive] = useState(-1);
  // What is being typed in a box that only chooses, or null while nobody is typing.
  const [query, setQuery] = useState<string | null>(null);
  // Whether anything was typed since the field took the focus: until then the whole list is offered.
  const [typedHere, setTypedHere] = useState(false);

  const root = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement | null>(null);
  const list = useRef<HTMLUListElement>(null);

  const text = free ? value : (query ?? value);
  const typed = free ? (typedHere ? value : '') : (query ?? '');
  const rows = fetched ? options : narrow(options, typed);

  // "Use 'what was typed'": only for something typed that no row already says.
  const typedText = value.trim();
  const useRow =
    free &&
    offerTyped &&
    typedText !== '' &&
    !options.some((option) => fold(option.label) === fold(typedText));
  const count = rows.length + (useRow ? 1 : 0);

  const open = focused && !dismissed;
  const expanded = open && count > 0;
  // A box that only chooses marks its best match as soon as something is typed, so Enter takes it.
  const marked = active >= 0 ? active : !free && typed !== '' && rows.length > 0 ? 0 : -1;
  const activeIndex = expanded && marked < count ? marked : -1;
  const optionId = (index: number) => `${ids.list}-${String(index)}`;

  // What the list says in place of rows: on its way, or that nothing matches.
  // Where typing is allowed nothing has to match, so only a box that chooses says that nothing does.
  const status =
    !open || count > 0 ? null : loading ? words.loading : typed !== '' && !free ? words.none : null;

  // Once, when the box is drawn: a button revealed it, and the next thing typed is the search.
  useEffect(() => {
    if (focusOnMount) input.current?.focus();
  }, [focusOnMount]);

  /*
   * The list is brought into view inside the box that scrolls the form, and
   * nothing else is scrolled: asking the browser to do it scrolls every box
   * above the field as well, the page behind a sheet included (the request of
   * 6 October 2026, on the sheet itself). Done again when a phone's keyboard
   * opens or closes, which changes how much of the form is in view.
   */
  useLayoutEffect(() => {
    if (!expanded) return undefined;

    const reveal = () => {
      const field = root.current;
      const scroller = field === null ? null : scrollerOf(field);
      if (field === null || scroller === null) return;

      const frame = scroller.getBoundingClientRect();
      const box = field.getBoundingClientRect();
      if (box.bottom <= frame.bottom) return;
      // As far as shows the whole list, and never so far that the field's own label leaves the top.
      const by = Math.min(box.bottom - frame.bottom + 8, box.top - frame.top - 4);
      if (by > 0) scroller.scrollTop += by;
    };

    reveal();
    const viewport = window.visualViewport;
    viewport?.addEventListener('resize', reveal);
    return () => viewport?.removeEventListener('resize', reveal);
  }, [expanded, count]);

  // The row the arrow keys are on stays in sight, inside the list's own scroll.
  useLayoutEffect(() => {
    const box = list.current;
    const row = activeIndex < 0 ? null : document.getElementById(`${ids.list}-${String(activeIndex)}`);
    if (box === null || row === null) return;

    if (row.offsetTop < box.scrollTop) box.scrollTop = row.offsetTop;
    else if (row.offsetTop + row.offsetHeight > box.scrollTop + box.clientHeight) {
      box.scrollTop = row.offsetTop + row.offsetHeight - box.clientHeight;
    }
  }, [activeIndex, ids.list]);

  function putAway() {
    setDismissed(true);
    setActive(-1);
    setQuery(null);
    setTypedHere(false);
  }

  function choose(index: number) {
    const option = rows[index];
    putAway();
    if (option !== undefined) onChoose(option);
    else if (index === rows.length && useRow) onUseTyped?.(typedText);
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    // A key pressed while a letter is still being composed belongs to the keyboard, not the list.
    if (event.nativeEvent.isComposing) return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      if (count === 0) return;
      event.preventDefault();
      const down = event.key === 'ArrowDown';
      // From no row at all, or from a list that was put away: the first row going down, the last going up.
      const from = expanded && active >= 0 ? active : down ? -1 : count;
      setDismissed(false);
      setActive((from + (down ? 1 : -1) + count) % count);
      return;
    }
    if (event.key === 'Enter') {
      if (activeIndex < 0) return;
      event.preventDefault();
      choose(activeIndex);
      return;
    }
    if (event.key === 'Escape' && open && (expanded || status !== null || query !== null)) {
      /*
       * The list goes and the key stops here. A sheet listens for Escape on
       * the document and would close along with the list, taking a form that
       * is half filled in with it.
       */
      event.preventDefault();
      event.stopPropagation();
      putAway();
      onDismiss?.();
    }
  }

  const described = [error !== null ? ids.problem : '', hint !== null ? ids.hint : '']
    .filter((each) => each !== '')
    .join(' ');
  const clearable = onClear !== undefined && text !== '';

  return (
    <div ref={root} className={`${modalStyles.field} ${styles.field} ${className ?? ''}`}>
      <label
        className={`${modalStyles.label} ${required ? styles.required : ''}`}
        htmlFor={inputId}
      >
        {label}
      </label>

      <div className={styles.box}>
        <input
          ref={(element) => {
            input.current = element;
            if (typeof inputRef === 'function') inputRef(element);
            else if (inputRef != null) inputRef.current = element;
          }}
          id={inputId}
          className={`${modalStyles.input} ${styles.input} ${clearable ? styles.clearable : ''}`}
          value={text}
          placeholder={placeholder}
          // The browser's own list of remembered words would sit over this one.
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode={inputMode}
          autoCapitalize={autoCapitalize}
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={expanded}
          aria-controls={expanded ? ids.list : undefined}
          aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined}
          aria-required={required || undefined}
          aria-invalid={error !== null || undefined}
          aria-describedby={described === '' ? undefined : described}
          onChange={(event) => {
            setDismissed(false);
            setActive(-1);
            setTypedHere(true);
            if (free) onType(event.target.value);
            else setQuery(event.target.value);
          }}
          onFocus={(event) => {
            setFocused(true);
            setDismissed(false);
            setTypedHere(false);
            // What is chosen is typed over, not added to.
            if (!free) event.target.select();
            onOpen?.();
          }}
          // Pressed again while it has the focus: the list comes back.
          onClick={() => setDismissed(false)}
          onBlur={() => {
            const wasOpen = open;
            setFocused(false);
            setActive(-1);
            setQuery(null);
            setTypedHere(false);
            if (wasOpen) onDismiss?.();
          }}
          onKeyDown={onKeyDown}
        />
        {clearable && (
          <button
            type="button"
            className={styles.clear}
            aria-label={words.clear(label)}
            // Keeps the focus in the input, which the next thing typed goes into.
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => {
              setQuery(null);
              setActive(-1);
              setDismissed(false);
              onClear();
              input.current?.focus();
            }}
          >
            <CloseIcon size={16} />
          </button>
        )}
      </div>

      {expanded && (
        <ul
          ref={list}
          id={ids.list}
          role="listbox"
          aria-label={label}
          className={styles.list}
          // Keeps the focus in the input, so the list is still there for the press, and for its scroll bar.
          onMouseDown={(event) => event.preventDefault()}
        >
          {rows.map((option, index) => (
            <li
              key={option.key}
              id={optionId(index)}
              role="option"
              aria-selected={index === activeIndex}
              className={`${styles.option} ${index === activeIndex ? styles.active : ''}`}
              onClick={() => choose(index)}
            >
              <span className={styles.optionLabel}>{option.label}</span>
              {option.detail != null && option.detail !== '' && (
                <span className={styles.optionDetail}>{option.detail}</span>
              )}
            </li>
          ))}
          {useRow && (
            <li
              id={optionId(rows.length)}
              role="option"
              aria-selected={rows.length === activeIndex}
              className={`${styles.option} ${styles.useTyped} ${
                rows.length === activeIndex ? styles.active : ''
              }`}
              onClick={() => choose(rows.length)}
            >
              <span className={styles.optionLabel}>{words.useTyped(typedText)}</span>
            </li>
          )}
        </ul>
      )}

      {/* Always there, so a screen reader hears the line arrive rather than a new element. */}
      <span className={`${modalStyles.hint} ${styles.status}`} role="status">
        {status ?? ''}
      </span>

      {error !== null && (
        <p id={ids.problem} className={modalStyles.error}>
          {error}
        </p>
      )}
      {hint !== null && (
        <span id={ids.hint} className={modalStyles.hint}>
          {hint}
        </span>
      )}
    </div>
  );
}
